<template>
  <div tabIndex="0" id="edit-project-template-page" @click.self="handleBackgroundClick()" class="page white" ref="projectWrapper">
    <el-container :class="['content-aside', { 'play-frame': projectTemplateFrameStatus !== projectTemplateFrameStatusOptions.EDIT_FRAME }]" id="projectTemplate" ref="projectFrame">

      <!-- Left Side -->
      <el-main class="cpl-main">
        <!-- Media Orientation -->
        <template v-if="showMediaOrientationIcon && (projectTemplate.hasOwnProperty('_edit') && projectTemplateFrameStatus === projectTemplateFrameStatusOptions.EDIT_FRAME)">
          <div class="orientation-options">
            <div class="items">
              <el-tooltip :popper-class="'custom-tooltip-popper-filters'" :show-arrow="false" effect="light" class="item" :content="$t('label.horizontalView')" placement="bottom">
                <div
                  @click="handleProjectOrientationClick(CONST.ORIENTATION_VALUE_HORIZONTAL)"
                  :class="{
                    'opacity-100!': projectOrientation === CONST.ORIENTATION_VALUE_HORIZONTAL,
                    'item horizontal': true
                  }">
                </div>
              </el-tooltip>

              <el-tooltip :popper-class="'custom-tooltip-popper-filters'" :show-arrow="false" effect="light" class="item" :content="$t('label.verticalView')" placement="bottom">
                <div
                  @click="handleProjectOrientationClick(CONST.ORIENTATION_VALUE_VERTICAL)"
                  :class="{
                    'opacity-100!': projectOrientation === CONST.ORIENTATION_VALUE_VERTICAL,
                    'item vertical': true
                  }">
                </div>
              </el-tooltip>
            </div>
          </div>
        </template>

        <!-- Project Template -->
        <el-row :gutter="20" class="project-row hide-scrollbar">
          <!-- Edit Project Name Field -->
          <div id="editProjectName">
            <div class="label">{{$t('label.projectName')}}</div>
            <div :class="['value', {'editing': editProjectName.editing}]">
              <el-input
                id="project-name-input"
                :model-value="editProjectName.value"
                @update:model-value="newValue => editProjectName.value = newValue"
                ref="editProjectNameInput"
                @blur="applyNewProjectTemplateName"
                class="cstm-input-grey">
              </el-input>
              <div @click="handleEditProjectNameCoverClick" class="cover">{{editProjectName.value}}</div>
            </div>
          </div>

          <!-- Project template workspace -->
          <div :class="['project-frame-content', {'vertical': (projectOrientation === 'vertical' && !customResolution.custom), 'custom-resolution': (customResolution.custom)}]">
            <div :class="['media-preview-fixed', {'firefox': ($utils.getBrowser() === 'Firefox')}]" :style="customResolution.ui.elStyle"
              ref="projectFrameWrapper">
              <template v-if="projectTemplate.hasOwnProperty('edit')">
                <iframe id="projectFrame"
                  v-if="['svg', 'html'].indexOf(projectTemplate.edit.type) >= 0"
                  :data-src="(projectTemplateFrameStatus === projectTemplateFrameStatusOptions.EDIT_FRAME ? $utils.trimDataSrc(projectTemplate.edit.url) : $utils.trimDataSrc(projectTemplate.play.url))"
                  :src="(projectTemplateFrameStatus === projectTemplateFrameStatusOptions.EDIT_FRAME ? `${$utils.filterMediaPreviewUrl(projectTemplate.edit.url)} ` : $utils.filterMediaPreviewUrl(projectTemplate.play.url, {queryParams: {preview: 1, dir: projectOrientation}}))"
                  @load="handleSVGOnLoad"
                  allow="clipboard-read *; clipboard-write *;"
                  :class="['object-fit_cover_center', 'template-target-on-refresh-token', `media-preview-${projectTemplate._edit.uid}`]">
                </iframe>

                <bezels-mask
                  v-if="bezelsPreviewInfo.bezels.length"
                  :bezels="bezelsPreviewInfo.bezels"
                  :resolutionWidth="bezelsPreviewInfo.width"
                  :resolutionHeight="bezelsPreviewInfo.height"
                  :cardWidth="customResolution.ui.width"
                  :cardHeight="customResolution.ui.height"
                  :fadeColor="projectTemplateFrameStatus === projectTemplateFrameStatusOptions.EDIT_FRAME" />

                <div class="no-preview" v-if="projectTemplate.edit.type === undefined">
                  <div class="desc">
                    <el-icon class="el-icon-warning"><WarningFilled /></el-icon>
                    <p>{{$t('label.previewUnavailable')}}</p>
                  </div>
                </div>
              </template>
            </div>
            <!-- TODO: put video-like play/stop/progress controls here -->
            <project-template-playback-controls
              v-if="projectTemplateFrameStatus === projectTemplateFrameStatusOptions.PLAY_FRAME"
              :preview-svg-getter="getPreviewSVG"
              ref="previewPlaybackControls" />
          </div>

          <div class="project-controls-wrapper w-full" ref="projectControls">
            <!-- Project Controls -->
            <project-template-controls
              :current-screen-index="currentScreenIndex"
              :overlayProperties="{ isVisible: showTopAddAppsOverlayIcon, isDisabled: !getOverlayFromApps }"
              :project-pages="{allPages: projectTemplate.data?.pages || [], allowedPages: allowedPages }"
              :visible="svgActivateClicks.isActivated && projectTemplateFrameStatus === projectTemplateFrameStatusOptions.EDIT_FRAME"
              @page-change="handleProjectPageChange($event)"
              @overlay-click="handleAddAppsOverlayClick"
            />
          </div>
        </el-row>
      </el-main>

      <el-aside class="cpl-aside" id="project-template-aside">
        <div class="content hide-scrollbar">
          <div v-if="projectTemplate.hasOwnProperty('_edit')" class="template-action-options">
            <!-- Settings Icon to show settings menu items -->
            <el-tooltip
              :popper-class="'custom-tooltip-popper capitalize-text'"
              :show-arrow="false"
              effect="light"
              :content="$t('label.edit')"
              :index="'projectTabIconTooltipEdit'"
              placement="bottom"
              class="item">
              <el-button @click="handleEditTemplateMenuItemsTabIcon(menuItemTabIds.settings)"
                :class="['cstm-icon-btn-medium', 'pencil_circle_grey', {'is-selected': projectTemplateFrameStatus === projectTemplateFrameStatusOptions.EDIT_FRAME && selectedEditFrameTab === menuItemTabIds.settings}]">
              </el-button>
            </el-tooltip>

            <!-- Edit Template Schedule -->
            <el-tooltip
              :popper-class="'custom-tooltip-popper capitalize-text'"
              :show-arrow="false"
              effect="light"
              :content="$t('label.preview')"
              :index="'projectTabIconTooltipPreview'"
              placement="bottom"
              class="item">
              <el-button @click="handleEditTemplateMenuItemsTabIcon(menuItemTabIds.templateSchedule)"
                :disabled="menuItemInProgressRequests.length > 0"
                :class="['cstm-icon-btn-medium', 'play_circle_grey', {'is-selected': projectTemplateFrameStatus === projectTemplateFrameStatusOptions.PLAY_FRAME && projectTemplateAction === projectTemplateActionOptions.EDIT_PROJECT_TEMPLATE}]">
              </el-button>
            </el-tooltip>
          </div>

          <!-- Edit Template Section -->
          <template v-if="projectTemplateFrameStatus === projectTemplateFrameStatusOptions.EDIT_FRAME && projectTemplate.hasOwnProperty('_edit')">
            <div id="editTemplateSection" class="edit-template-section">
              <!-- Add Apps -->
              <div id="addApps">
                <div v-if="addAppsTabMenuItems.length" class="apps-wrapper section-wrapper">
                  <label class="apps-wrapper-label">{{$t('label.apps')}}</label>
                  <div v-for="(appItem, indx) in addAppsTabMenuItems" :key="indx" :class="['app-button-wrapper', `${appItem.icon.replace(/\./g,'-')}-wrapper`]">
                    <el-tooltip
                      :key="indx"
                      :popper-class="'custom-tooltip-popper'"
                      :show-arrow="false"
                      effect="light"
                      :content="appItem.tooltip"
                      placement="bottom"
                      class="item">
                      <el-button
                        :class="['app-button', `${appItem.icon.replace(/\./g,'-')}`, `${((appItem.disabled) ? 'disabled': '')}`, $utils.getBrowser()]"
                        @click="handleAddAppsItemClick(appItem)">
                      </el-button>
                    </el-tooltip>
                    <div :class="`app-button-wrap ${appItem.icon.replace(/\./g,'-')}`"> </div>
                  </div>
                </div>
              </div>

              <!-- Edit Project Template Menu Items -->
              <edit-project-template-menu ref="projectSettings"
                :projectTemplateActionProp="projectTemplateAction"
                :openMediaPickerDialogProp="openMediaPickerDialogData"
                :screenIndexProp="currentScreenIndex"
                :projectTemplateProp="projectTemplate"
                :editTemplateFrameTabProp="selectedEditFrameTab"
                :tagsSummaryForSuggestionsProp="tagsSummaryForSuggestions"
                :isAlignElementVisible="isAlignElementVisible"
                @menu-edit-template-change="handleEditProjectTemplateChange($event)">
              </edit-project-template-menu>

              <!-- Submit Button -->
              <div ref="submitButtonWrapper" class="submit-btn-wrapper">
                <el-button :disabled="menuItemInProgressRequests.length > 0"
                  @click="handleSubmitProjectTemplateDataToBackend('submitTemplate')" class="cstm-btn-grey width-125 as-next-btn white-background">
                  {{$t('label.next')}}
                </el-button>
              </div>
            </div>
          </template>

          <!-- Edit Project Media Details -->
          <template v-if="(projectTemplateFrameStatus === projectTemplateFrameStatusOptions.PLAY_FRAME) && (projectTemplateAction === projectTemplateActionOptions.EDIT_PROJECT_TEMPLATE)">
            <div id="editMediaSection hide-scrollbar" class="edit-media-section">
              <media-details-menu v-if="projectMediaDetails !== null && userSchedulesDoneFetching"
                ref="projectDetailsMenu"
                :accessorComponentProp="'projectPage'"
                :mediaPropertiesToHideProp="['delete']"
                :mediaSelectionProp="[projectMediaDetails]"
                :userSchedulesProp="(projectTemplate.templateId === 'led') ? userSchedulesFilteredByResolution : userSchedules"
                :isMediaAlertTypeProp="projectMediaDetails.alert"
                :tagsSummaryProp="tagsSummaryForSuggestions"
                @media-menu-change="menuMediaDetailsOnChange($event)">
              </media-details-menu>

              <!-- Submit Button -->
              <div ref="submitButtonWrapper" v-if="(userSchedulesDoneFetching)" class="submit-btn-wrapper">
                <el-button @click="handleSubmitProjectTemplateDataToBackend('submitMedia')" class="cstm-btn-gradient width-125">
                  <template v-if="projectMediaDetails && projectMediaDetails.alertSubmitBtn">
                    <template v-if="projectMediaDetails.alertSubmitBtn === 'activate'"> {{$t('label.activate')}} </template>
                    <template v-else-if="projectMediaDetails.alertSubmitBtn === 'reactivate'"> {{$t('label.reactivate')}} </template>
                    <template v-else> {{$t('label.ok')}} </template>
                  </template>
                  <template v-else>
                    {{$t('label.ok')}}
                  </template>
                </el-button>
              </div>
            </div>
          </template>
        </div>
      </el-aside>
    </el-container>

    <div v-if="svgActivateClicks.isActivated && projectTemplateFrameStatus === projectTemplateFrameStatusOptions.EDIT_FRAME" :class="['project-overview-panel animated', {'slideInUp': isOverviewVisible }]" ref="projectOverview">
      <div class="project-overview-arrow" @click="handleOverviewClick"></div>
      <div class="overview-container">
        <project-template-pages
          :class="{'is-alert': projectTemplate.alert}"
          :projectTemplateProp="projectTemplate"
          :currentPageNumberProp="currentScreenIndex"
          :menuItemInProgressRequestsProp="menuItemInProgressRequests"
          :projectOrientationProp="projectOrientation"
          :projectTemplatePagesIFramePreviewProp="projectTemplatePagesIFramePreview"
          :tagsSummaryForSuggestionsProp="tagsSummaryForSuggestions"
          @project-template-page-change="handleProjectPageChange($event)">
        </project-template-pages>
      </div>
    </div>
  </div>
</template>

<script src="./project-template.js"></script>
<style lang="scss" src="./project-template.scss"></style>
